import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton", "tbody", "template", "addRowButton", "guestCheckbox"]
  static values = { minPlayers: Number, maxPlayers: Number, allGuests: Boolean, maxGuests: Number, editable: Boolean }

  connect() {
    this.activeRowIndex = null
    this.addingRow = false
    this.shirtNumberDebounceTimeout = null
    this.updateSubmitButtonState()
    this.updateAddRowButtonState()
    this.guestChanged()
  }

  disconnect() {
    clearTimeout(this.shirtNumberDebounceTimeout)
  }

  addRow() {
    if (this.addingRow || this.rowCount() >= this.maxPlayersValue) return
    this.addingRow = true

    // Derived from the live DOM row count (rather than a separately tracked
    // counter) so it can never drift out of sync with what's actually rendered.
    const index = this.rowCount()
    const html = this.templateTarget.innerHTML
      .replace(/__INDEX__/g, index)
      .replace(/__DISPLAY_INDEX__/g, index + 1)

    const wrapper = document.createElement('tbody')
    wrapper.innerHTML = html.trim()
    const row = wrapper.firstElementChild
    this.tbodyTarget.appendChild(row)

    // The new row's select has data-controller="tom-select" on it, so
    // Stimulus enhances it automatically as soon as it's connected — no
    // manual re-initialization needed.

    if (this.allGuestsValue) {
      this.lockGuestCheckbox(row)
    } else {
      this.guestChanged()
    }

    this.updateAddRowButtonState()
    this.addingRow = false
  }

  // Divisions where every player counts as a guest (e.g. Masters, Veterans)
  // force the checkbox on and prevent it from being unchecked.
  lockGuestCheckbox(row) {
    const hiddenInput = row.querySelector('input[type="hidden"][name$="[is_guest]"]')
    const checkbox = row.querySelector('input[type="checkbox"][name$="[is_guest]"]')

    if (hiddenInput) hiddenInput.value = '1'
    if (checkbox) {
      checkbox.checked = true
      checkbox.disabled = true
    }
  }

  // Divisions with a capped guest count: once that many checkboxes are
  // checked, the remaining unchecked ones are disabled until one is freed up.
  guestChanged() {
    if (this.allGuestsValue || !this.editableValue) return

    const checkboxes = this.guestCheckboxTargets
    const atLimit = checkboxes.filter((checkbox) => checkbox.checked).length >= this.maxGuestsValue

    checkboxes.forEach((checkbox) => {
      if (!checkbox.checked) checkbox.disabled = atLimit
    })
  }

  rememberRow(event) {
    this.activeRowIndex = event.params.rowIndex
  }

  playerChanged(event) {
    this.toggleRowButtons(event.target)
  }

  // Debounced so typing "10" doesn't briefly flag a duplicate against an
  // existing "1" while the second digit is still being typed.
  shirtNumberChanged() {
    clearTimeout(this.shirtNumberDebounceTimeout)
    this.shirtNumberDebounceTimeout = setTimeout(() => this.updateSubmitButtonState(), 800)
  }

  validateForm(event) {
    if (this.checkDuplicates()) event.preventDefault()
  }

  // Flags rows whose selected player or shirt number is repeated elsewhere in
  // the visible form, so duplicates are caught before submit instead of
  // failing server-side validation and wiping every entered row.
  checkDuplicates() {
    const rows = Array.from(this.element.querySelectorAll('tr[data-row-index]'))

    const playerCounts = {}
    const shirtCounts = {}

    rows.forEach((row) => {
      const select = row.querySelector('select')
      const shirtInput = row.querySelector('input[type="number"]')

      if (select && select.value) playerCounts[select.value] = (playerCounts[select.value] || 0) + 1
      if (shirtInput && shirtInput.value !== '') shirtCounts[shirtInput.value] = (shirtCounts[shirtInput.value] || 0) + 1
    })

    let hasDuplicate = false

    rows.forEach((row) => {
      const select = row.querySelector('select')
      const shirtInput = row.querySelector('input[type="number"]')

      const playerDuplicate = !!(select && select.value && playerCounts[select.value] > 1)
      const shirtDuplicate = !!(shirtInput && shirtInput.value !== '' && shirtCounts[shirtInput.value] > 1)

      this.toggleInvalid(select, playerDuplicate, 'Player already added to this squad')
      this.toggleInvalid(shirtInput, shirtDuplicate, 'Shirt number already in use')

      if (playerDuplicate || shirtDuplicate) hasDuplicate = true
    })

    return hasDuplicate
  }

  toggleInvalid(el, isInvalid, message) {
    if (!el) return

    el.classList.toggle('is-invalid', isInvalid)
    const cell = el.closest('td')
    const feedback = cell.querySelector('.invalid-feedback')

    if (isInvalid && !feedback) {
      const div = document.createElement('div')
      div.className = 'invalid-feedback d-block'
      div.textContent = message
      cell.appendChild(div)
    } else if (!isInvalid && feedback) {
      feedback.remove()
    }
  }

  clearPlayer(event) {
    const row = event.target.closest('tr')
    const select = row && row.querySelector('select')
    if (!select) return

    if (select.tomselect) {
      select.tomselect.clear()
    } else {
      select.value = ''
    }

    const guestCheckbox = row.querySelector('input[type="checkbox"][name$="[is_guest]"]')
    if (guestCheckbox && !this.allGuestsValue) guestCheckbox.checked = false

    this.toggleRowButtons(select)
    this.guestChanged()
  }

  playerCreated(event) {
    const player = event.detail && event.detail.player
    if (!player || this.activeRowIndex === null) return

    const row = this.element.querySelector(`tr[data-row-index="${this.activeRowIndex}"]`)
    const select = row && row.querySelector('select')

    if (select && select.tomselect) {
      select.tomselect.addOption(player)
      select.tomselect.setValue(player.id)
      this.toggleRowButtons(select)
    }

    this.activeRowIndex = null
  }

  toggleRowButtons(selectEl) {
    const row = selectEl.closest('tr')
    if (row) {
      const hasPlayer = !!selectEl.value
      const addBtn = row.querySelector('.add-new-player-btn')
      const clearBtn = row.querySelector('.clear-player-btn')

      if (addBtn) addBtn.style.display = hasPlayer ? 'none' : ''
      if (clearBtn) clearBtn.style.display = hasPlayer ? '' : 'none'
    }

    this.updateSubmitButtonState()
  }

  rowCount() {
    return this.element.querySelectorAll('tr[data-row-index]').length
  }

  updateSubmitButtonState() {
    const hasDuplicates = this.checkDuplicates()
    if (!this.hasSubmitButtonTarget) return

    const filledCount = Array.from(this.element.querySelectorAll('tr[data-row-index] select'))
      .filter((select) => !!select.value).length

    this.submitButtonTarget.disabled = filledCount < this.minPlayersValue || hasDuplicates
  }

  updateAddRowButtonState() {
    if (!this.hasAddRowButtonTarget) return

    this.addRowButtonTarget.disabled = this.rowCount() >= this.maxPlayersValue
  }
}
